import './flow.css';
import { useMemo } from 'react';
import type { Snapshot } from '../../api/types';
import type { TopologyView } from '../../api/installation';
import { deriveFlowState, verbruikGridpoortVandaagKwh } from '../../lib/derive';
import { fmtKw, fmtKwBol, fmtKwh } from '../../lib/format';
import { useMediaQuery } from '../../lib/useMediaQuery';
import { useWallClockTick } from '../../hooks/useWallClockTick';
import {
  COMPACT_ZONDER_PV_POS, DESKTOP_ZONDER_PV_POS, LIJN_DESKTOP_ZONDER_PV, DakBollen, DakLijnen, DakPillen, GRID_ANCHOR_INV_MET_DAKEN, layoutDaken, LIJN_COMPACT_ZONDER_PV,
  VIEWBOX_BREEDTE_MET_DAKEN,
} from './RemoteDakNodes';
import {
  SunIcon, GridIcon, HouseIcon, ShieldHouseIcon, BatteryIcon, soc_of_onbekend,
} from './icons';
const C_MASTER_BATT_STROKE = '#86efac'; // matcht master-batt circle bij slaveLive

// Formatteer snapshot-leeftijd als "X s geleden" / "Xs ago" voor staleness-indicator.
// Geeft operators direct inzicht: data > 30s oud = inverter/Pi-probleem.
// Terugval op lege string als lastUpdateIso ontbreekt.
// Geëxporteerd voor unit-tests.
export function fmtSnapshotAge(
  lastUpdateIso: string | undefined,
  nowMs: number = Date.now(),
): { ageStr: string; stale: boolean } {
  if (!lastUpdateIso) return { ageStr: '', stale: false };
  const ageMs = nowMs - new Date(lastUpdateIso).getTime();
  const ageSec = Math.round(ageMs / 1000);
  const stale = ageSec > 30; // Pi mist ≥6 poll-ticks (5s elk) → P1 indicator
  const ageStr = ageSec < 60
    ? `${ageSec}s geleden`
    : `${Math.round(ageSec / 60)}min geleden`;
  return { ageStr, stale };
}

// Vite serveert public/ onder de configureerde base. Die base hier niet
// hardcoderen: dezelfde bron wordt ook onder een andere prefix gebouwd om twee
// versies naast elkaar te kunnen draaien, en een vast '/v2/' levert daar een
// gebroken afbeelding midden in het stroomdiagram op.
const LOGO_URL = `${import.meta.env.BASE_URL}zbs-logo.png`;

// Single source of truth for circle geometry — circles render from this AND
// flow paths derive from it, so visuals stay in sync.
//
// Layout: PV / INV / Battery zitten verticaal in 1 kolom op cx=310 (DC-keten).
// Grid / Load / Backup zitten verticaal in 1 kolom op cx=500 (AC-keten),
// 190px van INV. Resultaat: rechte verticale pijlen door de DC-keten en
// vrijgekomen ruimte rechts (viewBox 800px) voor een 3e KPI-kolom met controls.
type Circle = { cx: number; cy: number; r: number };
const C = {
  pv:     { cx: 310, cy: 75,  r: 32 },
  inv:    { cx: 310, cy: 290, r: 50 },
  batt:   { cx: 310, cy: 505, r: 32 },
  grid:   { cx: 500, cy: 75,  r: 32 },
  load:   { cx: 500, cy: 290, r: 36 },
  backup: { cx: 500, cy: 505, r: 32 },
} as const satisfies Record<string, Circle>;

// Slave-cluster — alleen gerenderd bij topology === 'master_slave'.
// Slave-inv staat op cx=170: uitgelijnd op het horizontale center van de
// PV-pill + Batterij-pill (beide x=30..310, midden x=170). Daardoor:
//   1) AC-bus slave-inv→master-inv loopt nu van x=200 tot x=260 (60px,
//      ruimschoots leesbaar). Voorheen op cx=200 was dat maar 30px.
//   2) Slave-inv hangt visueel onder de "PV/Batt-kolom" links, in plaats
//      van halverwege tussen pillen en master in het niemandsland.
// Slave-batt blijft op cx=246 — strak tegen master-batt aan zodat de
// "tweede battery-bank naast master"-metafoor klopt; de DC-bus van slave
// loopt dan diagonaal van inv (cx=170) naar batt (cx=246).
// Kleur volgt slaveLive: groen bij Modbus-link, slate-grijs zonder.
const C_SLAVE = {
  inv:  { cx: 170, cy: 290, r: 30 },  // x=140..200 (gap 60px naar master_inv left edge x=260)
  batt: { cx: 246, cy: 505, r: 32 },  // x=214..278; right edge raakt master_batt left (278)
} as const satisfies Record<string, Circle>;

const round = (n: number) => n.toFixed(1);
const deg2rad = (d: number) => (d * Math.PI) / 180;

// Anchor: point on the circle's edge at `angDeg` (screen coords, 0=right, 90=down).
// Choosing the anchor angle explicitly lets us pick a tangent direction that's
// both visually pleasing AND radially aligned (= no kink at the connection).
function anchor(c: Circle, angDeg: number): [number, number] {
  const a = deg2rad(angDeg);
  return [c.cx + c.r * Math.cos(a), c.cy + c.r * Math.sin(a)];
}

// Control point at `dist` outside the anchor along the same outward direction.
// Bezier tangent at endpoint = (endpoint − adjacent control), so when the
// outward direction matches the circle's radial at that anchor, the line
// exits/enters the bol perpendicular to its rim.
function outward(p: [number, number], angDeg: number, dist: number): [number, number] {
  const a = deg2rad(angDeg);
  return [p[0] + dist * Math.cos(a), p[1] + dist * Math.sin(a)];
}

// Cubic Bezier between two circles with explicit anchor angles + control distance.
// Anchor is on each circle's edge at the chosen angle; control point sits `d`
// outside along the same outward direction. Reverse just flips M/control order
// so the marker-end (arrow) appears on the other endpoint.
function flowBezier(
  a: Circle, b: Circle, angA: number, angB: number, d = 80, reverse = false,
): string {
  const p0 = anchor(a, angA);
  const p3 = anchor(b, angB);
  const c1 = outward(p0, angA, d);
  const c2 = outward(p3, angB, d);
  if (reverse) {
    return `M ${round(p3[0])} ${round(p3[1])} C ${round(c2[0])} ${round(c2[1])}, ${round(c1[0])} ${round(c1[1])}, ${round(p0[0])} ${round(p0[1])}`;
  }
  return `M ${round(p0[0])} ${round(p0[1])} C ${round(c1[0])} ${round(c1[1])}, ${round(c2[0])} ${round(c2[1])}, ${round(p3[0])} ${round(p3[1])}`;
}

function flowStraight(a: Circle, b: Circle, angA: number, angB: number): string {
  const p0 = anchor(a, angA);
  const p1 = anchor(b, angB);
  return `M ${round(p0[0])} ${round(p0[1])} L ${round(p1[0])} ${round(p1[1])}`;
}

// === COMPACT-MODE (mobile) ===
// Bram-instructie 2026-05-17: "symbolen, lijnen, radiussen identiek aan desktop;
// pillen weg en opschonen". Geen aparte geometry meer (was Solis-stijl 480x480);
// compactMode hergebruikt nu de desktop `C` constants + flow-paths + slave-
// cluster en cropt alleen de pill-zones via een nauwere viewBox. Pillen worden
// conditioneel weggelaten via {!compactMode && ...}.

interface Props {
  snapshot: Snapshot | null;
  lastUpdateIso?: string;
  /** Optioneel: render "Uitloggen" link naast de Laatste-update-regel. */
  onLogout?: () => void;
  /** Topology uit /api/config/installation. null = nog niet geladen; default
   *  rendering = single-inverter. Bij master_slave verschijnt slave-cluster. */
  topology?: TopologyView | null;
  /** installation.pv_kwp_nominal === 0: de accu-omvormer heeft zelf geen PV (Erdotex). */
  geenEigenPv?: boolean;
}

export function FlowSVG({ snapshot, lastUpdateIso, onLogout, topology, geenEigenPv }: Props) {
  const s = snapshot ?? {};
  const f = deriveFlowState(s);
  // Snapshot-leeftijd hangt aan de WANDKLOK, niet aan de tijdstempel die hij
  // bewaakt. Tot wachtrij #181 memoiseerde deze regel op `lastUpdateIso` alleen;
  // viel de regellus stil terwijl de API bleef antwoorden, dan veranderde die
  // tijdstempel niet meer en toonde de kop eeuwig "(3s geleden)" in zwart -
  // de 30-secondendrempel kon per constructie niet meer vuren. De tick zorgt dat
  // leeftijd en stale-vlag ook zonder nieuwe data oplopen; de memo blijft staan
  // zodat de DOM-node alleen bij een echte verandering herschreven wordt.
  const nowMs = useWallClockTick(5_000);
  const { ageStr, stale } = useMemo(
    () => fmtSnapshotAge(lastUpdateIso, nowMs),
    [lastUpdateIso, nowMs],
  );
  const isMasterSlave = topology?.topology === 'master_slave';
  const totalPacks = topology?.total_packs ?? 2;
  const displayCapKwh = topology?.display_capacity_kwh ?? 0;

  // PV: master+slave site-totaal indien beschikbaar (master_slave VJ); op
  // single-inverter NS valt _pv_total_power_w terug op pv_power_w (= master).
  const pvW = (s._pv_total_power_w ?? s.pv_power_w ?? 0);
  // Master battery circle toont **MASTER-ONLY 2-pack aggregate**. Slave-batt
  // circle toont slave-only 2-pack aggregate. Site-totaal is zichtbaar op de
  // Batterij-pill onderaan ("Nu ±X kW") zodat de mentale grid-match nog werkt
  // zonder asymmetrie tussen de twee batterij-cirkels.
  //
  // Eerder (commit 0e41cd4 → revert 2026-05-14): master-cirkel toonde
  // _battery_total_power_w om "21 kW vs grid 42 kW" mentaal te matchen.
  // Bram bug-rapport 2026-05-14: master=site-totaal + slave=slave-only
  // gaf structurele asymmetrie (40,8 master + 19,5 slave = onzin-som,
  // slave dubbelgeteld). Per-bank symmetrie is duidelijker; site-totaal
  // staat één plek lager op de pill.
  //
  // Fallback-keten: _master_battery_total → battery_power_w (pack-1 raw)
  // voor pre-master-slave-fix snapshots.
  const battW = (s._master_battery_total_power_w ?? s.battery_power_w ?? 0);
  // Site-totaal voor Batterij-pill (master+slave aggregate). NS (single):
  // _battery_total == master-only via server-fallback (api.py).
  const siteBattW = (s._battery_total_power_w ?? s._master_battery_total_power_w ?? s.battery_power_w ?? 0);
  // `_battery_total_power_known === false`: de server kon het sitetotaal
  // niet vaststellen en stuurt een terugval (één bank, of 0 zonder data). Dat
  // getal hoort niet onder het sitelabel van de pill; die toont dan '—', net
  // als onbekende SOC en onbekend verbruik (wachtrij #200).
  const siteBattKnown = s._battery_total_power_known !== false;
  const gridW = (s.meter_total_power_w ?? 0);
  // Verbruik-circle: gebruik grid_port_load_w (server-side energiebalans-residu excl.
  // backup) als dat aanwezig is. Op VJ is household_load_w ≈ backup_load_w door
  // hardware-routing via EPS-circuit — zonder dit veld tonen Verbruik en Backup
  // identieke waarden. Fallback naar household_load_w voor NS (single-inverter,
  // geen backup-circuit, server stuurt grid_port_load_w nog niet of ≈ household).
  //
  // `null` is hier een DERDE toestand naast "0 kW" en "een getal": de server
  // kon het verbruik niet afleiden omdat het accu-vermogen geen richting had.
  // Een `?? 0` zou daar "0 kW" van maken, en dat is een bewering die we niet
  // kunnen waarmaken — de tegel toont dan '—'.
  // `_verbruik_onmeetbaar` (alleen AC-sites) wint van elke terugval: op
  // Erdotex is household_load_w hetzelfde PV-residu (~25 kW, review 22-10-2026).
  const loadW: number | null = s._verbruik_onmeetbaar
    ? null
    : (s.grid_port_load_w ?? s.household_load_w ?? null);
  // Sitetotaal (master + slave EPS-poort). backup_load_w is alleen de master:
  // op VJ toonde de cirkel 1,9 kW terwijl de slave-poort er 10,9 kW bij leverde
  // (1-10-2026 21:55, accu's -13 kW, net -0,3 kW) en de balans niet sloot.
  const backupW = (s._backup_load_total_w ?? s.backup_load_w ?? 0);
  // Dagregel van de Verbruik-pil: 33179 min backup, niet teller 33586 (#940).
  const verbruikVandaagKwh = verbruikGridpoortVandaagKwh(s);
  // Pack-aware SOC (audit 2026-05-27 root-cause fix): _site_soc_pct = capaciteit-
  // gewogen gemiddelde van ALLE packs = waar de EMS-engine/safety op stuurt;
  // _master/_slave = per-inverter gemiddelde (2 packs elk) voor de aparte circles.
  // Rauwe `soc` (33139 = master PACK 1) alleen fallback voor oude snapshots —
  // voorheen toonde het hoofdscherm pack-1 → verborg een pack-uitval en week af
  // van de EMS-beslissing. Op NS (1 inverter) is _master_soc_pct == _site_soc_pct.
  // `null` = onbekend (geen bron, of alleen een sentinel buiten 0-100): het icoon
  // en de SOC-tekst tonen dan '—', net als `loadW` hierboven (wachtrij #336).
  const masterSoc = soc_of_onbekend(s._master_soc_pct, s.soc);
  const siteSoc = soc_of_onbekend(s._site_soc_pct, s.soc);

  // #4.2 — per-pack divergentie-badge op het hoofdscherm. Drempel 20pp =
  // backend PACK_IMBALANCE_WARN_THRESHOLD_PCT (main.py); zo zien we een
  // pack-uitval (zoals NS pack-2 op 2026-05-26: bevroor 98% terwijl pack-1
  // alleen droeg) niet alleen op /tech + Telegram maar ook hier in één oogopslag.
  const PACK_DIVERGE_PP = 20;
  // Een sentinel buiten 0-100 is geen pack-stand: dan is de kloof onbekend, geen badge.
  const packGap = (a: unknown, b: unknown): number | null => {
    const pa = soc_of_onbekend(a, null);
    const pb = soc_of_onbekend(b, null);
    return pa !== null && pb !== null ? Math.abs(pa - pb) : null;
  };
  const masterPackGap = packGap(s.soc, s.bms2_soc);
  const slavePackGap = packGap(s.slave_soc, s.slave_bms2_soc);
  const masterPackDiverged = masterPackGap != null && masterPackGap >= PACK_DIVERGE_PP;
  const slavePackDiverged = slavePackGap != null && slavePackGap >= PACK_DIVERGE_PP;

  // De "Verbruik"-cirkel toont grid-port-zijde verbruik. Single-inverter (NS):
  // grid_port_load_w via AC-balans = echt huishoudverbruik → altijd tonen, label
  // "Verbruik" (zoals SolisCloud). Master-slave (VJ): grid_port_load_w is daar een
  // energiebalans-residu uit master-only telemetrie (geen betrouwbaar verbruik;
  // ~0,6 kW = conversieverlies + meetruis). Bram 2026-05-27 (optie A): verberg de
  // tegel op VJ als < 1 kW; toon 'm gewoon als "Verbruik" zodra ≥ 1 kW.
  const loadLabel = 'Verbruik';
  // Server-side reden waarom het verbruik niet uit de balans volgt (alleen
  // AC-sites, snapshot_enrichment.verbruik_onmeetbaar_reden). Erdotex 22-10-2026:
  // de CT ziet alleen de tak van de accu-omvormer, dus verbruik en netsaldo
  // zijn onbekend; een getal zou de PV zijn die de meter niet ziet.
  const verbruikOnmeetbaar = s._verbruik_onmeetbaar ?? null;
  const netOnbekend = verbruikOnmeetbaar === 'meter_omvormertak';
  const onmeetbaarTekst = verbruikOnmeetbaar === null ? null
    : (netOnbekend ? 'Niet meetbaar: geen netmeting' : 'Niet meetbaar: PV-meting onvolledig');
  // Onbekend verbruik blijft zichtbaar (als '—'): een verborgen tegel leest als
  // "er is niets", en juist dat willen we hier niet beweren.
  const showLoadNode = !isMasterSlave || loadW === null || loadW >= 1000;

  // Slave-inverter live observability (master-slave parallel-mode).
  // Threshold 100W om register-ruis te dempen — onder dit niveau geen flow-arrow.
  const slaveLive = isMasterSlave && typeof s.slave_read_ts === 'string';
  // Slave battery W: server-side 2-pack aggregate (sign × V × ΣI + master-sign-
  // fallback). Fallback: raw slave_battery_power_w = PACK 1 ONLY (Solis quirk,
  // ~half van actual). Zie docs/PITFALLS.md #27.
  const slaveBattW = (s._slave_battery_total_power_w ?? s.slave_battery_power_w ?? 0);
  const slaveAcW = (s.slave_ac_active_power_w ?? 0);
  const slaveSoc = soc_of_onbekend(s._slave_soc_pct, s.slave_soc);
  const slavePvW = (s.slave_pv_power_w ?? 0);
  const slaveBattCharging = slaveLive && slaveBattW > 100;
  const slaveBattDischarging = slaveLive && slaveBattW < -100;
  const slaveAcToMaster = slaveLive && slaveAcW > 100;   // slave levert naar master via AC-bus
  const slaveAcFromMaster = slaveLive && slaveAcW < -100; // master voedt slave via AC-bus
  // Slave-PV: nog niet geïnstalleerd op VJ (sept 2026 plan), maar dashboard
  // is voorbereid — zodra slave eigen PV-strings krijgt en > 100W oplevert,
  // rendert er een diagonale PV→slave-inv flow vanuit hetzelfde PV-symbool.
  const slavePvActive = slaveLive && slavePvW > 100;

  // Slave-kleur volgt Modbus-link: groen #22c55e online, slate #64748b offline.
  // Bewust dezelfde tint als de master-battery flow-paths (#22c55e) — slave is
  // óók een battery-bank, en de groene "alive" tint visualiseert dat de bank
  // observability heeft. Wordt op stroke + icon-fill toegepast (inclusief glyph).
  const slaveColor = slaveLive ? '#22c55e' : '#64748b';
  const slaveIconFill = slaveLive ? '#22c55e' : '#94a3b8';
  const slaveTextColor = slaveLive ? '#16a34a' : '#64748b';

  // Flow paths — anchor angles chosen so the natural "flowy" tangent at each
  // bol is ALSO the radial direction → curve sweeps freely without a kink.
  // Angle convention: 0=right, 90=down, 180=left, 270=up.
  //
  // DC-keten (PV ↓ INV ↓ Battery): rechte verticale pijlen — ze staan in
  // dezelfde kolom (cx=310). DISC-pad wordt vanaf Battery gebouwd zodat de
  // marker-end op INV uitkomt.
  // AC-keten (INV → Grid/Load/Backup): horizontaal voor Load, S-curve voor
  // Grid (omhoog) en Backup (omlaag) met INV's diagonale hoeken.
  const PATH_PV_INV         = flowStraight(C.pv,   C.inv,    90, 270);    // PV ↓ → INV
  // PV → slave-inv: diagonale bezier vanuit PV-cirkel naar slave-inverter
  // (cx=170, cy=290). Anchor 135° = down-left van PV, 315° = up-right van
  // slave-inv → bezier zwiept netjes diagonaal zonder kink.
  const PATH_PV_INV_SLAVE   = flowBezier(C.pv,    C_SLAVE.inv, 135, 315);
  const PATH_INV_BATT_CHRG  = flowStraight(C.inv,  C.batt,   90, 270);    // INV ↓ → Battery
  const PATH_INV_BATT_DISC  = flowStraight(C.batt, C.inv,   270,  90);    // Battery ↑ → INV
  const PATH_INV_LOAD       = flowStraight(C.inv,  C.load,    0, 180);    // INV → Load
  const PATH_INV_GRID_EXP   = flowBezier(C.inv,    C.grid,  315,  90);    // INV ↗ diagonaal, Grid ↓ verticaal
  const PATH_INV_GRID_IMP   = flowBezier(C.inv,    C.grid,  315,  90, 80, true);
  const PATH_INV_BACKUP     = flowBezier(C.inv,    C.backup, 45, 270);    // INV ↘ diagonaal, Backup ↑ verticaal

  // compactMode: <=900px → FlowSVG zonder pillen (mobile). Drie-niveau systeem
  // (Bram 2026-05-18 v2):
  //  - ≥1501: 3-kolom hero-row met volle FlowSVG (CSS-grid + pillen)
  //  - 901-1500: stacked layout (children flex column) met FlowSVG full-width
  //    INCL. pillen → tussen-laag waar tegels onder zitten maar pillen
  //    leesbaar blijven
  //  - ≤900: stacked + FlowSVG compactMode (pillen weg, alleen cirkels)
  // CSS-stack drempel (1500) en TSX-compactMode drempel (900) bewust apart —
  // zie layout.css comment voor onderbouwing per niveau.
  const compactMode = useMediaQuery('(max-width: 900px)');
  // viewBox-crop op het cirkel-cluster (master+slave). Master kolommen op
  // cx=310/500 met r≤50, slave-inv cx=170 r=30, slave-batt cx=246 r=32.
  // Top circles y=43 (PV cy-r), bottom circles y=537 (batt cy+r). 10-15px
  // margin zodat shadow-filter en eventuele text labels niet clippen.
  // Erdotex (remote_pv): de daken krijgen een vierde kolom rechts, dus de
  // viewBox groeit alleen in de breedte. Zonder remote_pv exact de oude
  // viewBox (VJ/NS/HO ongewijzigd).
  // Een geconfigureerde bron telt ook zonder omvormerlijst: een meet-Pi die nog
  // nooit online was (Erdotex 19-10-2026) levert geen `inverters`, en dan viel
  // het hele dashboard terug op de oude PV-layout.
  const remotePv = s.remote_pv && (s.remote_pv.sources ?? []).length > 0
    ? s.remote_pv : null;
  // Telefoon (compact): geen dakbollen; de PV-bol toont het sitetotaal en krijgt één lijn naar Grid.
  // Zonder eigen PV staan de daken ook op desktop links (zie RemoteDakNodes).
  const dakenKolom = !!remotePv && !compactMode && !geenEigenPv;
  // Geen eigen PV aan de accu-omvormer (config, niet een 0 W-meting) en wel
  // remote_pv: de reguliere PV-node vervalt; op de telefoon nemen de daken
  // die plek in, onder elkaar, elk met een eigen lijn naar Grid.
  const zonderEigenPv = !!geenEigenPv && !!remotePv;
  const daken = dakenKolom && remotePv ? layoutDaken(remotePv)
    : (zonderEigenPv && remotePv
      ? layoutDaken(remotePv, compactMode ? COMPACT_ZONDER_PV_POS : DESKTOP_ZONDER_PV_POS) : []);
  const remotePvActief = !!remotePv && compactMode && !zonderEigenPv && (s.remote_pv_power_w ?? 0) >= 100;
  const viewBoxAttr = dakenKolom
    ? `0 0 ${VIEWBOX_BREEDTE_MET_DAKEN} 580`
    : (compactMode ? '125 35 425 520' : '0 0 800 580');
  // Met daken komt de accu-omvormer van linksonder binnen op Grid, zodat de
  // dakenlijnen rechtsonder ruimte hebben (zie RemoteDakNodes).
  const pathInvGridExp = dakenKolom ? flowBezier(C.inv, C.grid, 315, GRID_ANCHOR_INV_MET_DAKEN) : PATH_INV_GRID_EXP;
  const pathInvGridImp = dakenKolom ? flowBezier(C.inv, C.grid, 315, GRID_ANCHOR_INV_MET_DAKEN, 80, true) : PATH_INV_GRID_IMP;
  const ariaLabel = compactMode ? 'Power flow (mobile)' : undefined;
  // Zonder eigen PV (Erdotex) voedt de accu-omvormer het pand niet rechtstreeks:
  // PV, accu en net komen samen op de aansluiting, en daar hangt de last aan.
  // De Verbruik-lijn loopt dan van Grid (net/gebouw) naar Verbruik, niet vanuit
  // de accu-omvormer (Bram 22-10-2026: "Verbruik moet ook een lijn uit grid
  // krijgen"). VJ/NS/HO houden de lijn vanuit de omvormer.
  const pathLoad = zonderEigenPv ? flowStraight(C.grid, C.load, 90, 270) : PATH_INV_LOAD;

  return (
    <>
      {lastUpdateIso && (
        <div className="last-update">
          {/* Snapshot-tijdstip + leeftijdsindicator. De exacte ISO-timestamp
              staat als title-tooltip — handig voor operators die willen
              weten of twee velden uit hetzelfde snapshot-moment komen.
              Bij stale (>30s) verschijnt een oranje kleur als P1 signaal. */}
          <span
            title={`Snapshot-timestamp: ${lastUpdateIso}`}
            style={stale ? { color: '#f97316', fontWeight: 600 } : undefined}
          >
            Laatste update: {new Date(lastUpdateIso).toLocaleString('nl-NL')}
            {ageStr && (
              <span style={{ marginLeft: 6, opacity: 0.7, fontSize: '10px' }}>
                ({ageStr})
              </span>
            )}
          </span>
          {onLogout && (
            <button
              type="button"
              className="logout-pill"
              onClick={onLogout}
              title="Uitloggen — volgende keer wachtwoord opnieuw invoeren"
              aria-label="Uitloggen"
            >
              <svg
                width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Uitloggen</span>
            </button>
          )}
        </div>
      )}
      <svg
        className={dakenKolom ? 'flow-svg flow-svg--daken' : 'flow-svg'}
        viewBox={viewBoxAttr}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        aria-label={ariaLabel}
      >
        <defs>
          <linearGradient id="g-pv" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#fef3c7" />
            <stop offset="60%" stopColor="#fffbeb" />
            <stop offset="100%" stopColor="#ffffff" />
          </linearGradient>
          <linearGradient id="g-grid" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%" stopColor="#fee2e2" />
            <stop offset="60%" stopColor="#fef2f2" />
            <stop offset="100%" stopColor="#ffffff" />
          </linearGradient>
          <linearGradient id="g-gridload" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%" stopColor="#ffedd5" />
            <stop offset="60%" stopColor="#fff7ed" />
            <stop offset="100%" stopColor="#ffffff" />
          </linearGradient>
          <linearGradient id="g-batt" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#dcfce7" />
            <stop offset="60%" stopColor="#f0fdf4" />
            <stop offset="100%" stopColor="#ffffff" />
          </linearGradient>
          <linearGradient id="g-backup" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%" stopColor="#fef3f2" />
            <stop offset="60%" stopColor="#fff7ed" />
            <stop offset="100%" stopColor="#ffffff" />
          </linearGradient>
          {remotePv && (
            <linearGradient id="g-pv-r" x1="1" y1="0" x2="0" y2="0">
              <stop offset="0%" stopColor="#fef3c7" />
              <stop offset="60%" stopColor="#fffbeb" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>
          )}
          <marker id="arr-pv"markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
            <path d="M0,0 L0,6 L7,3 z" fill="#f59e0b" />
          </marker>
          <marker id="arr-load" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
            <path d="M0,0 L0,6 L7,3 z" fill="#f97316" />
          </marker>
          <marker id="arr-batt" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
            <path d="M0,0 L0,6 L7,3 z" fill="#22c55e" />
          </marker>
          <marker id="arr-grid" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
            <path d="M0,0 L0,6 L7,3 z" fill="#ef4444" />
          </marker>
          <filter id="circle-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.2" floodColor="#000" floodOpacity="0.08" />
          </filter>
        </defs>

        {/* Pills volgen hun cirkel: links = pill EINDIGT op cx, rechts = pill BEGINT op cx.
            In compactMode (mobile) worden alle pillen weggelaten — alleen cirkels, lijnen
            en slave-cluster blijven, met smallere viewBox. */}
        {!compactMode && (
        <>
        {/* PV pill (links) */}
        {!zonderEigenPv && (<>
        <rect x={C.pv.cx - 280} y="30" width="280" height="90" rx="45" fill="url(#g-pv)" stroke="#fbbf24" strokeWidth="0.7" />
        <text x={C.pv.cx - 161} y="60" textAnchor="middle" fill="#1f2937" fontWeight="600" fontSize="13">PV</text>
        <text x={C.pv.cx - 161} y="79" textAnchor="middle" fill="#6b7280" fontSize="11">Vandaag opgewekt</text>
        <text x={C.pv.cx - 161} y="103" textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="17">
          {fmtKwh(s._pv_total_today_kwh ?? s.pv_today_kwh ?? 0)}
        </text>
        </>)}

        {/* Battery pill (links). Tekst is topology-aware en links-uitgelijnd
            bij master_slave zodat het slave-circle (cx=200, r=24, x=176..224)
            niet over het label valt. Single-mode behoudt bestaande centering.
            SOC% staat in master BatteryIcon — niet dupliceren in label bij ms. */}
        <rect x={C.batt.cx - 280} y="460" width="280" height="90" rx="45" fill="url(#g-batt)" stroke="#86efac" strokeWidth="0.7" />
        <text x={isMasterSlave ? 120 : C.batt.cx - 161} y="490" textAnchor="middle" fill="#1f2937" fontWeight="600" fontSize="13">Batterij</text>
        <text x={isMasterSlave ? 120 : C.batt.cx - 161} y="509" textAnchor="middle" fill="#6b7280" fontSize="11"
          data-batt-site-state={siteBattKnown ? 'known' : 'unknown'}>
          {(() => {
            // Site-totaal live richting expliciet in pill.
            // siteBattW > 100  → "↓ LADEN X kW" (groen, laden)
            // siteBattW < -100 → "↑ ONTLADEN X kW" (oranje, ontladen)
            // anders            → "STAND-BY" (grijs)
            const THRESH = 100;
            // JSX.Element | string — no React import needed (react-jsx transform)
            let dirLabel: JSX.Element | string;
            if (!siteBattKnown) {
              dirLabel = (
                <tspan fill="#9ca3af">
                  <title>Sitetotaal batterijvermogen niet vastgesteld: tweede bank of batterijdata ontbreekt</title>
                  — kW (sitetotaal onbekend)
                </tspan>
              );
            } else if (siteBattW > THRESH) {
              dirLabel = (
                <>
                  <tspan fill="#16a34a" fontWeight="700">↓ LADEN</tspan>
                  {' '}{fmtKw(Math.abs(siteBattW))}
                </>
              );
            } else if (siteBattW < -THRESH) {
              dirLabel = (
                <>
                  <tspan fill="#ea580c" fontWeight="700">↑ ONTLADEN</tspan>
                  {' '}{fmtKw(Math.abs(siteBattW))}
                </>
              );
            } else {
              dirLabel = 'STAND-BY';
            }
            const prefix = isMasterSlave
              ? `${displayCapKwh.toFixed(0)} kWh · `
              : `×${totalPacks} · SOC ${siteSoc === null ? '—' : `${siteSoc.toFixed(0)}%`} · `;
            return <>{prefix}{dirLabel}</>;
          })()}
        </text>
        <text x={isMasterSlave ? 120 : C.batt.cx - 161} y="533" textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13">
          {(() => {
            // Site-totaal (master+slave) als beschikbaar, anders master-only fallback.
            // IN = geladen vandaag, UIT = ontladen vandaag — expliciet i.p.v. netto.
            const chr = s._battery_total_charge_today_kwh ?? s.battery_charge_today_kwh ?? 0;
            const dis = s._battery_total_discharge_today_kwh ?? s.battery_discharge_today_kwh ?? 0;
            return `↓ IN ${chr.toFixed(0)} / ↑ UIT ${dis.toFixed(0)} kWh`;
          })()}
        </text>

        {/* Grid pill (rechts).
            Live richting (meter_total_power_w sign):
              > 50W  → "↑ EXPORT X kW" (groen: teruglevering aan net)
              < -50W → "↓ IMPORT X kW" (rood: afname van net)
              anders → "STAND-BY" (grijs)
            Dagelijkse totalen op regel 3: "↓ IN: X kWh  ↑ UIT: Y kWh".
            Drempel 50W = afgeleid van deriveFlowState (gridExporting/Importing). */}
        <rect x={C.grid.cx} y="30" width="280" height="90" rx="45" fill="url(#g-grid)" stroke="#fca5a5" strokeWidth="0.7" />
        <text x={C.grid.cx + 161} y="60" textAnchor="middle" fill="#1f2937" fontWeight="600" fontSize="13">Grid</text>
        <text x={C.grid.cx + 161} y="79" textAnchor="middle" fill="#6b7280" fontSize="11">
          {(() => {
            // gridW = meter_total_power_w; + = export, - = import
            // CT alleen op de accutak: wat hij meet is niet het netsaldo.
            if (netOnbekend) return <tspan fill="#6b7280">niet gemeten (geen P1)</tspan>;
            const THRESH = 50;
            if (gridW > THRESH) {
              return (
                <>
                  <tspan fill="#16a34a" fontWeight="700">↑ EXPORT</tspan>
                  {' '}{fmtKw(Math.abs(gridW))}
                </>
              );
            }
            if (gridW < -THRESH) {
              return (
                <>
                  <tspan fill="#dc2626" fontWeight="700">↓ IMPORT</tspan>
                  {' '}{fmtKw(Math.abs(gridW))}
                </>
              );
            }
            return <tspan fill="#6b7280">STAND-BY</tspan>;
          })()}
        </text>
        <text x={C.grid.cx + 161} y="103" textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13">
          {`${netOnbekend ? 'accu ' : ''}↓ IN: ${(s.grid_import_today_kwh ?? 0).toFixed(1)} / ↑ UIT: ${(s.grid_export_today_kwh ?? 0).toFixed(1)} kWh`}
        </text>

        {/* Verbruik-pill (rechts). Op master-slave verborgen als < 1 kW (residu). */}
        {showLoadNode && (
        <>
        <rect x={C.load.cx} y="245" width="280" height="90" rx="45" fill="url(#g-gridload)" stroke="#fdba74" strokeWidth="0.7" />
        <text x={C.load.cx + 161} y="275" textAnchor="middle" fill="#1f2937" fontWeight="600" fontSize="13">{loadLabel}</text>
        <text x={C.load.cx + 161} y="294" textAnchor="middle" fill="#6b7280" fontSize="11">
          {/* MAJOR 3: grid-poortdeel 0 en backup>0 → load loopt via EPS-bus. */}
          {onmeetbaarTekst ?? (verbruikVandaagKwh === 0 && (s.backup_load_today_kwh ?? 0) > 0
            ? 'via EPS-bus'
            : 'Vandaag verbruikt')}
        </text>
        <text x={C.load.cx + 161} y="318" textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="17">
          {onmeetbaarTekst ? '—' : fmtKwh(verbruikVandaagKwh)}
        </text>
        </>
        )}

        {/* Backup pill (rechts) */}
        <rect x={C.backup.cx} y="460" width="280" height="90" rx="45" fill="url(#g-backup)" stroke="#fdba74" strokeWidth="0.7" />
        <text x={C.backup.cx + 161} y="490" textAnchor="middle" fill="#1f2937" fontWeight="600" fontSize="13">Backup</text>
        <text x={C.backup.cx + 161} y="509" textAnchor="middle" fill="#6b7280" fontSize="11">Vandaag verbruikt</text>
        <text x={C.backup.cx + 161} y="533" textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="17">
          {fmtKwh(s.backup_load_today_kwh ?? 0)}
        </text>
        {daken.length > 0 && <DakPillen daken={daken} links={!dakenKolom} />}
        </>
        )}

        {/* PV → INV. Erdotex (remote_pv, geen lokale PV): de PV loopt via de
            omvormer-bollen naar Grid, niet door de accu-omvormer → inactieve lijn. */}
        {!zonderEigenPv && (f.pvActive && !(remotePv && (s.pv_power_w ?? 0) + (s.slave_pv_power_w ?? 0) <= 100) ? (
          <>
            <path d={PATH_PV_INV} stroke="#fbbf24" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={PATH_PV_INV} stroke="#f59e0b" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-pv)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </>
        ) : (
          <path d={PATH_PV_INV} stroke="#cbd5e1" strokeWidth="1.4" fill="none" strokeDasharray="3 5" opacity="0.6" />
        ))}

        {/* INV ↔ Grid */}
        {f.gridExporting ? (
          <>
            <path d={pathInvGridExp} stroke="#fca5a5" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={pathInvGridExp} stroke="#ef4444" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-grid)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </>
        ) : f.gridImporting ? (
          <>
            <path d={pathInvGridImp} stroke="#fca5a5" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={pathInvGridImp} stroke="#ef4444" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-grid)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </>
        ) : (
          <path d={pathInvGridExp} stroke="#cbd5e1" strokeWidth="1.4" fill="none" strokeDasharray="3 5" opacity="0.6" />
        )}

        {/* INV → GridLoad (verborgen op master-slave als < 1 kW) */}
        {showLoadNode && (f.loadActive ? (
          <>
            <path d={pathLoad} stroke="#fb923c" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={pathLoad} stroke="#f97316" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="10 12" markerEnd="url(#arr-load)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </>
        ) : (
          <path d={pathLoad} stroke="#cbd5e1" strokeWidth="1.4" fill="none" strokeDasharray="3 5" opacity="0.6" />
        ))}

        {/* INV ↔ Battery */}
        {f.battCharging ? (
          <>
            <path d={PATH_INV_BATT_CHRG} stroke="#86efac" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={PATH_INV_BATT_CHRG} stroke="#22c55e" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-batt)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </>
        ) : f.battDischarging ? (
          <>
            <path d={PATH_INV_BATT_DISC} stroke="#86efac" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={PATH_INV_BATT_DISC} stroke="#22c55e" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-batt)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </>
        ) : (
          <path d={PATH_INV_BATT_CHRG} stroke="#cbd5e1" strokeWidth="1.4" fill="none" strokeDasharray="3 5" opacity="0.6" />
        )}

        {/* INV → Backup */}
        {f.backupActive ? (
          <>
            <path d={PATH_INV_BACKUP} stroke="#fdba74" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={PATH_INV_BACKUP} stroke="#f97316" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-load)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </>
        ) : (
          <path d={PATH_INV_BACKUP} stroke="#cbd5e1" strokeWidth="1.4" fill="none" strokeDasharray="3 5" opacity="0.6" />
        )}

        {/* Slave-cluster — alleen bij master_slave. PARALLEL-TWIN layout:
            slave-inv en slave-batt staan op dezelfde Y als master, met
            zichtbare gap waar de bus loopt. Render VOOR master-nodes zodat
            paths achter master liggen (master overlapt geen text). */}
        {isMasterSlave && (() => {
          // AC-bus geometrie (horizontaal slave-inv → master-inv).
          const acStart: [number, number] = [C_SLAVE.inv.cx + C_SLAVE.inv.r, C_SLAVE.inv.cy];
          const acEnd: [number, number] = [C.inv.cx - C.inv.r + 1, C.inv.cy];
          const acMid: [number, number] = [(acStart[0] + acEnd[0]) / 2, acStart[1]];
          // Path-strings, beide richtingen (zelfde lijn, marker-end op verschillende kant).
          const acPathFromSlave = `M ${acStart[0]} ${acStart[1]} L ${acEnd[0]} ${acEnd[1]}`;
          const acPathFromMaster = `M ${acEnd[0]} ${acEnd[1]} L ${acStart[0]} ${acStart[1]}`;
          // Battery-bus (slave-inv ↓ slave-batt, bezier zoals master). kW-label
          // langs deze bus is verwijderd — slave-batt circle toont nu z'n eigen
          // kW eronder (gelijke behandeling als master-batt).
          const battPathCharge = flowBezier(C_SLAVE.inv, C_SLAVE.batt, 90, 270, 80);
          const battPathDisch = flowBezier(C_SLAVE.batt, C_SLAVE.inv, 270, 90, 80);
          return (
          <g className="slave-cluster">
            {/* PV → slave-inv: alleen renderen als slave eigen PV-strings heeft
                (slave_pv_power_w > 100W). Bram 2026-05-14: VJ heeft nu geen
                slave-PV (alle 50 kWp op master), komt later. Dashboard is
                voorbereid — zodra slave_pv_power_w binnenkomt, rendert hier
                een diagonale flow vanaf hetzelfde PV-symbool. */}
            {slavePvActive && !zonderEigenPv && (
              <>
                <path d={PATH_PV_INV_SLAVE} stroke="#fbbf24" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
                <path d={PATH_PV_INV_SLAVE} stroke="#f59e0b" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-pv)">
                  <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
                </path>
              </>
            )}
            {/* AC-bus slave-inv ↔ master-inv (Master↔Slave parallel-koppeling).
                Live-flow als slave-direct read actief, anders dashed grijs. */}
            {slaveAcToMaster ? (
              <>
                <path d={acPathFromSlave} stroke="#fb923c" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
                <path d={acPathFromSlave} stroke="#f97316" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-load)">
                  <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
                </path>
              </>
            ) : slaveAcFromMaster ? (
              <>
                <path d={acPathFromMaster} stroke="#fb923c" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
                <path d={acPathFromMaster} stroke="#f97316" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-load)">
                  <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
                </path>
              </>
            ) : (
              <path d={acPathFromSlave} stroke="#94a3b8" strokeWidth="1.8" strokeDasharray="4 3" fill="none" opacity="0.7" />
            )}
            {/* AC-bus kW label — alleen bij live + flow > drempel. */}
            {(slaveAcToMaster || slaveAcFromMaster) && (
              <text
                x={acMid[0]} y={acMid[1] - 8}
                textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="11"
              >{fmtKw(Math.abs(slaveAcW))}</text>
            )}

            {/* Battery-bus slave-inv ↔ slave-batt. */}
            {slaveBattCharging ? (
              <>
                <path d={battPathCharge} stroke="#86efac" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
                <path d={battPathCharge} stroke="#22c55e" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-batt)">
                  <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
                </path>
              </>
            ) : slaveBattDischarging ? (
              <>
                <path d={battPathDisch} stroke="#86efac" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
                <path d={battPathDisch} stroke="#22c55e" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-batt)">
                  <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
                </path>
              </>
            ) : (
              <path d={battPathCharge} stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 4" fill="none" opacity="0.6" />
            )}
            {/* Geen kW-label meer langs battery-bus — slave-batt cirkel toont
                z'n eigen kW eronder (gelijk aan master), label hier was dubbel. */}

            {/* Slave inverter — kleur volgt slaveLive. Stroke SOLID bij live,
                DASHED bij offline ("geen Modbus-link"). Bliksem-glyph inside. */}
            <circle
              cx={C_SLAVE.inv.cx} cy={C_SLAVE.inv.cy} r={C_SLAVE.inv.r}
              fill="#f8fafc" stroke={slaveColor} strokeWidth="1.6"
              strokeDasharray={slaveLive ? undefined : "5 3"}
              filter="url(#circle-shadow)"
            />
            <path
              d={`M ${C_SLAVE.inv.cx - 4} ${C_SLAVE.inv.cy - 9} L ${C_SLAVE.inv.cx - 7} ${C_SLAVE.inv.cy + 1} L ${C_SLAVE.inv.cx - 1} ${C_SLAVE.inv.cy + 1} L ${C_SLAVE.inv.cx - 3} ${C_SLAVE.inv.cy + 9} L ${C_SLAVE.inv.cx + 7} ${C_SLAVE.inv.cy - 2} L ${C_SLAVE.inv.cx + 1} ${C_SLAVE.inv.cy - 2} L ${C_SLAVE.inv.cx + 4} ${C_SLAVE.inv.cy - 9} Z`}
              fill={slaveIconFill} opacity={slaveLive ? 0.9 : 0.7}
            />
            <text
              x={C_SLAVE.inv.cx} y="248"
              textAnchor="middle" fill={slaveTextColor} fontWeight="700" fontSize="11"
              letterSpacing="0.12em"
            >SLAVE</text>
            {!slaveLive && (
              <text
                x={C_SLAVE.inv.cx} y="338"
                textAnchor="middle" fill="#94a3b8" fontSize="9" fontStyle="italic"
              >geen Modbus-link</text>
            )}

            {/* Slave battery — visueel GELIJK aan master-batt bij slaveLive:
                BatteryIcon (SOC-gevuld glyph + intern % label) + kW-tekst
                eronder. Bij offline: dashed slate-cirkel + static glyph zoals
                voor abd7ea0. SOC-divergence master/slave is zichtbaar via
                BatteryIcon's eigen % (red<20 / orange<50 / green≥50). */}
            <circle
              cx={C_SLAVE.batt.cx} cy={C_SLAVE.batt.cy} r={C_SLAVE.batt.r}
              fill="#fff"
              stroke={slaveLive ? C_MASTER_BATT_STROKE : slaveColor}
              strokeWidth="1.4"
              strokeDasharray={slaveLive ? undefined : "5 3"}
              filter="url(#circle-shadow)"
            />
            {slaveLive ? (
              <>
                <BatteryIcon cx={C_SLAVE.batt.cx} cy={C_SLAVE.batt.cy - 10} socPct={slaveSoc} />
                <text
                  x={C_SLAVE.batt.cx} y={C_SLAVE.batt.cy + 20}
                  textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13"
                >{fmtKw(slaveBattW)}</text>
              </>
            ) : (
              <>
                <rect
                  x={C_SLAVE.batt.cx - 9} y={C_SLAVE.batt.cy - 5}
                  width="16" height="10" rx="1.5"
                  fill="none" stroke={slaveColor} strokeWidth="1.3"
                />
                <rect
                  x={C_SLAVE.batt.cx + 7} y={C_SLAVE.batt.cy - 2.5}
                  width="2.5" height="5" rx="0.5" fill={slaveColor}
                />
              </>
            )}
          </g>
          );
        })()}

        {daken.length > 0 && <DakLijnen daken={daken} grid={C.grid} geo={dakenKolom ? undefined : (compactMode ? LIJN_COMPACT_ZONDER_PV : LIJN_DESKTOP_ZONDER_PV)} />}
        {remotePvActief && (
          <>
            <path d={flowStraight(C.pv, C.grid, 0, 180)} stroke="#fbbf24" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={flowStraight(C.pv, C.grid, 0, 180)} stroke="#f59e0b" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-pv)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </>
        )}

        {/* Circles — geometry from C constant so paths stay in sync */}
        {!zonderEigenPv && (<>
        <circle cx={C.pv.cx} cy={C.pv.cy} r={C.pv.r} fill="#fff" stroke="#fbbf24" strokeWidth="1.4" filter="url(#circle-shadow)" />
        <SunIcon cx={C.pv.cx} cy={C.pv.cy - 10} color="#f59e0b" />
        <text x={C.pv.cx} y={C.pv.cy + 20} textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13">{fmtKwBol(pvW)}</text>
        </>)}

        <circle cx={C.grid.cx} cy={C.grid.cy} r={C.grid.r} fill="#fff" stroke="#fca5a5" strokeWidth="1.4" filter="url(#circle-shadow)" />
        <GridIcon cx={C.grid.cx} cy={C.grid.cy - 10} color="#ef4444" />
        <text x={C.grid.cx} y={C.grid.cy + 20} textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13">{netOnbekend ? '—' : fmtKwBol(gridW)}</text>

        <circle cx={C.inv.cx} cy={C.inv.cy} r={C.inv.r} fill="#fff" stroke="#94a3b8" strokeWidth="1.4" filter="url(#circle-shadow)" />
        <image href={LOGO_URL} x={C.inv.cx - 38} y={C.inv.cy - 38} width="76" height="76" preserveAspectRatio="xMidYMid meet" />

        {showLoadNode && (
        <>
        <circle cx={C.load.cx} cy={C.load.cy} r={C.load.r} fill="#fff" stroke="#fdba74" strokeWidth="1.4" filter="url(#circle-shadow)" />
        <HouseIcon cx={C.load.cx} cy={C.load.cy - 8} color="#f97316" />
        <text x={C.load.cx} y={C.load.cy + 20} textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13">{loadW === null ? '—' : fmtKwBol(loadW)}</text>
        </>
        )}

        <circle cx={C.batt.cx} cy={C.batt.cy} r={C.batt.r} fill="#fff" stroke="#86efac" strokeWidth="1.4" filter="url(#circle-shadow)" />
        <BatteryIcon cx={C.batt.cx} cy={C.batt.cy - 10} socPct={masterSoc} />
        <text x={C.batt.cx} y={C.batt.cy + 20} textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13">{fmtKwBol(battW)}</text>

        <circle cx={C.backup.cx} cy={C.backup.cy} r={C.backup.r} fill="#fff" stroke="#cbd5e1" strokeWidth="1.4" filter="url(#circle-shadow)" />
        <ShieldHouseIcon cx={C.backup.cx} cy={C.backup.cy - 8} color="#9ca3af" />
        <text x={C.backup.cx} y={C.backup.cy + 20} textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13">{fmtKwBol(backupW)}</text>

        {daken.length > 0 && <DakBollen daken={daken} label={compactMode} />}

        {/* #4.2 — per-pack divergentie-badges (≥20pp), renderen bovenop de batterij-cirkels.
            Master rechtsboven; slave linksboven (om de master-cirkel niet te raken). */}
        {masterPackDiverged && (
          <g>
            <circle cx={C.batt.cx + 24} cy={C.batt.cy - 22} r="9" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
            <text x={C.batt.cx + 24} y={C.batt.cy - 18} textAnchor="middle" fill="#fff" fontWeight="700" fontSize="12">!</text>
            <title>{`${isMasterSlave ? 'Master-' : ''}packs ${masterPackGap!.toFixed(0)} pp uiteen — mogelijk pack-uitval. Check /tech + Solis-cloud.`}</title>
          </g>
        )}
        {slavePackDiverged && (
          <g>
            <circle cx={C_SLAVE.batt.cx - 24} cy={C_SLAVE.batt.cy - 22} r="9" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
            <text x={C_SLAVE.batt.cx - 24} y={C_SLAVE.batt.cy - 18} textAnchor="middle" fill="#fff" fontWeight="700" fontSize="12">!</text>
            <title>{`Slave-packs ${slavePackGap!.toFixed(0)} pp uiteen — mogelijk pack-uitval. Check /tech + Solis-cloud.`}</title>
          </g>
        )}
      </svg>
    </>
  );
}
